import React from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { determineLender } from '@atc/wallet-payment-fns';

import { SubHeading } from 'reaxl';
import { OfferBreakdown, TaxesAndFees } from 'reaxl-native-deal-calculator';
import { LenderPayment } from 'reaxl-wallet';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

/**
 * @typedef {Object} OfferSummaryConfig
 * @property {boolean} [disableTrade=false] - Whether to disable trade-in functionality
 * @property {boolean} [enableTaxesAndFees=false] - Whether to enable taxes and fees display
 * @property {number} [listingPrice] - Fallback listing price if sale price and MSRP are not available
 */

/**
 * OfferSummary component displays a breakdown of vehicle offer details including
 * pricing, payments, taxes, fees, and trade-in information. It supports different payment types
 * (cash, finance, lease) and dynamically updates based on user selections and available data.
 *
 * @param {Object} props - Component props
 * @param {OfferSummaryConfig} [props.config={}] - Configuration options for the offer summary
 * @param {boolean} [props.isBreakdownConfirmation=false] - Whether this is being displayed in a confirmation context
 * @returns {React.ReactElement} The rendered OfferSummary component
 */
function OfferSummary({
    config = {},
    isBreakdownConfirmation = false,
}) {
    const dispatch = useDispatch();

    const {
        disableTrade = false,
        enableTaxesAndFees = false,
        listingPrice: fallbackListingPrice,
    } = config;

    const {
        id,
        pricingDetail: { salePrice, msrp } = {},
        type,
        paymentServices: { digitalRetailingType } = {},
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};

    const { value: tradeInValue = 0, amountOwed = 0 } = useSelector(paymentsDuck.selectors.getTradeIn) || {};

    const { offerPrice } = useSelector(paymentsDuck.selectors.getPencilingValues) || {};

    // Get payments
    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const financingInfo = useSelector((state) => paymentsDuck.selectors.getCustomLenderPencilingPaymentByListingId(state, id));
    const leasingInfo = useSelector((state) => paymentsDuck.selectors.getLowestLeasePaymentByListingId(state, id));
    const {
        pricingDetails: cashPricingDetails,
        taxesFees: { totalTaxesFees: cashTaxesFees } = {},
    } = useSelector((state) => paymentsDuck.selectors.getLowestCashPaymentByListingId(state, id));

    const {
        dealType,
        downPayment,
        interestRate,
        isLoading,
        interactions: {
            hasRequestedTaxesFees = false,
            isNationalRates = false,
        } = {},
    } = paymentsInfo;

    const isFinance = dealType === 'finance';
    const isLease = dealType === 'lease';
    const isCash = dealType === 'cash';

    const {
        downPayment: financeDownPayment = 0,
        offeredBy = '',
        payment: financePayment,
        pricingDetails: financePricingDetails,
        taxesFees: { totalTaxesFees: financeTaxesFees } = {},
    } = financingInfo;
    const {
        acquisitionFee,
        capCost: { adjustedCapCost, capitalizedTaxesAndFees } = {},
        downPayment: dueAtSigningValue = 0,
        payment: leasePayment,
        pricingDetails: leasePricingDetails,
        dueInAdvance: { firstPayment, cashDueFromCustomer, diaTaxes, diaDealerFees, diaGovFees, diaLenderFees } = {},
        taxesFees: { totalTaxesFees: leaseTaxesFees } = {},
    } = leasingInfo;

    const getTotalTaxesFees = () => {
        if (!enableTaxesAndFees) return 0;
        return Math.floor(({
            cash: cashTaxesFees,
            finance: financeTaxesFees,
            lease: leaseTaxesFees,
        }[dealType])) || 0;
    };

    const listingType = type && type.toLowerCase();
    const vehicleCondition = listingType === 'certified' ? 'used' : listingType;

    const customRate = interestRate?.custom;
    const rate = customRate ?? financingInfo?.[vehicleCondition] ?? interestRate?.[vehicleCondition] ?? 4.5;

    const price = offerPrice || salePrice || msrp || fallbackListingPrice;

    const totalEquity = tradeInValue - amountOwed;
    const totalTaxesFees = getTotalTaxesFees();
    const totalCashOut = price - totalEquity + totalTaxesFees;
    const totalFinancing = price - totalEquity - (downPayment || financeDownPayment) + totalTaxesFees;
    const diaFee = (diaDealerFees ?? 0) + (diaGovFees ?? 0) + (diaLenderFees ?? 0);

    const getPricingBreakdown = (tabName) => {
        const pricingDetails = {
            cash: cashPricingDetails,
            lease: leasePricingDetails,
            finance: financePricingDetails,
        };

        const pricingBreakdown = { ...pricingDetails[tabName] };

        pricingBreakdown.manufacturerOffer = pricingBreakdown.rebate;
        pricingBreakdown.offersUpgrades = pricingBreakdown.specialOfferValue;

        if (pricingBreakdown.salePrice) {
            pricingBreakdown.listingPrice = pricingBreakdown.salePrice;
        }

        if (offerPrice) {
            pricingBreakdown.offerPrice = offerPrice;
        }

        return pricingBreakdown;
    };

    const handleFallbackPayment = (fallbackPayment) => {
        dispatch(paymentsDuck.creators.updatePaymentPencils({ fallbackPayment }));
    };

    const renderLenderPayment = () => {
        if (isLoading) return null;

        if (isCash || isFinance && totalFinancing < 0) {
            return (
                <LenderPayment
                    key="cashPayment"
                    price={isFinance ? 0 : Math.max(0, totalCashOut)}
                    uiContext="cash"
                />
            );
        }
        return (
            <LenderPayment
                key="lendingPayment"
                paymentsInfo={paymentsInfo}
                price={price}
                uiContext="calculator"
                vehicleCondition={vehicleCondition}
                {...(isFinance && {
                    handleFallbackPayment,
                    interestRate: rate,
                    lender: determineLender(customRate, offeredBy),
                    personalizedPayment: financePayment,
                    showLenderDisclaimer: false,
                })}
                {...(isLease && {
                    dealType: 'lease',
                    personalizedPayment: leasePayment,
                    showLenderDisclaimer: false,
                })}
            />
        );
    };

    const renderTaxesAndFees = () => (
        <TaxesAndFees
            key="snapshotTaxesAndFees"
            totalTaxesFees={totalTaxesFees}
            isNationalRates={isNationalRates}
            enableTaxesAndFees={enableTaxesAndFees}
            includeTaxesAndFees={hasRequestedTaxesFees}
            uiContext="snapshot"
            activeTab={dealType}
            isEstimate={digitalRetailingType === 'default'}
        />
    );

    return (
        <>
            {!isBreakdownConfirmation && renderLenderPayment()}
            {isBreakdownConfirmation && (
                <SubHeading
                    size={300}
                    className="margin-bottom-3"
                >
                    Offer Breakdown
                </SubHeading>
            )}

            <OfferBreakdown
                amountOwed={amountOwed}
                disableTrade={disableTrade}
                getPricingBreakdown={getPricingBreakdown}
                tradeInValue={tradeInValue}
                paymentsInfo={paymentsInfo}
                dueAtSigningValue={downPayment || dueAtSigningValue}
                renderTaxesAndFees={renderTaxesAndFees}
                tabName={dealType}
                diaTax={diaTaxes}
                diaFee={diaFee}
                firstPayment={firstPayment}
                cashDueFromCustomer={cashDueFromCustomer}
                adjustedCapCost={adjustedCapCost}
                isEstimate={digitalRetailingType === 'default'}
                capitalizedTaxesAndFees={capitalizedTaxesAndFees}
                uiContext={isBreakdownConfirmation ? 'confirmation' : 'default'}
                {...(isFinance && {
                    downPayment: downPayment || financeDownPayment,
                    financePayment,
                    financeRate: rate,
                    lender: determineLender(customRate, offeredBy),
                    totalFinancing,
                })}
                {...(isLease && {
                    acquisitionFee,
                    adjustedCapCost,
                    leasePayment,
                })}
                {...(isCash && {
                    totalCashOut,
                })}
            />
        </>
    );
}

export default OfferSummary;
